import { useMemo } from 'react';
import { dShort, statusColors, type Week } from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { api } from '../api';
import { BTN_SOFT, Empty, SERIF_H_LG } from '../components/ui';

function trailOf(w: Week): string {
  const bits: string[] = [];
  if (w.submittedAt) bits.push(`submitted by ${w.submittedBy || '—'} on ${dShort(w.submittedAt)}`);
  if (w.approvedAt && w.status === 'approved')
    bits.push(`approved by ${w.approvedBy || '—'} on ${dShort(w.approvedAt)}`);
  if (w.returnedAt && w.status === 'returned')
    bits.push(`returned by ${w.returnedBy || '—'} on ${dShort(w.returnedAt)}`);
  return bits.length ? bits.join(' · ') : 'No approval action yet';
}

export function Approvals({ onOpen }: { onOpen: (weekId: string) => void }) {
  const { engine, can, mutate } = useApp();
  const rows = useMemo(() => engine.sortedWeeks().slice().reverse(), [engine]);
  const canApprove = can('approve');
  const canEntry = can('entry');

  const intro = canApprove
    ? 'Each weekly return submitted by the Field Officer is listed here. Nothing enters the dashboard or any report until you approve it. Returning a week sends it back for correction with your remark attached.'
    : 'Your weekly returns and where each one stands. Submit a week once it is complete; the PMU In-charge will approve it or return it with remarks.';

  return (
    <div
      style={sx(
        'max-width:1000px;margin:0 auto;padding:20px 16px 60px;display:flex;flex-direction:column;gap:16px',
      )}
    >
      <div>
        <div style={sx(SERIF_H_LG)}>Approval of weekly returns</div>
        <div style={sx('font-size:13px;color:#5a6b68;line-height:1.5;max-width:720px')}>{intro}</div>
      </div>

      {rows.map((w) => {
        const c = statusColors(w.status);
        return (
          <div
            key={w.id}
            style={sxc(
              'background:#fff;border:1px solid #dbe3e0;border-radius:8px;padding:16px;display:flex;gap:14px;flex-wrap:wrap;align-items:center',
              `border-left:4px solid ${c.fg}`,
            )}
          >
            <div style={sx('flex:1;min-width:230px;display:flex;flex-direction:column;gap:4px')}>
              <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap')}>
                <div style={sx('font-size:15px;font-weight:700')}>Week {w.no}</div>
                <span
                  style={sxc(
                    'padding:3px 9px;border-radius:99px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em',
                    `background:${c.bg}`,
                    `color:${c.fg}`,
                  )}
                >
                  {w.status || 'draft'}
                </span>
              </div>
              <div style={sx('font-size:12.5px;color:#5a6b68')}>
                {dShort(w.start)} to {dShort(w.end)} · {w.doc || '—'}
              </div>
              <div style={sx('font-size:12.5px;color:#33403d')}>{trailOf(w)}</div>
              {w.reviewNote ? (
                <div
                  style={sx(
                    'font-size:12.5px;color:#a3372c;background:#fbeae7;padding:7px 10px;border-radius:4px',
                  )}
                >
                  Returned: {w.reviewNote}
                </div>
              ) : null}
            </div>
            <div style={sx('display:flex;gap:8px;flex-wrap:wrap')}>
              <button type="button" onClick={() => onOpen(w.id)} style={sx(BTN_SOFT)}>
                Open
              </button>
              {canApprove && w.status === 'submitted' ? (
                <button
                  type="button"
                  onClick={() => void mutate(() => api.approveWeek(w.id))}
                  style={sx(
                    'background:#2f7d51;color:#fff;border:0;padding:9px 14px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer',
                  )}
                >
                  Approve
                </button>
              ) : null}
              {canApprove && (w.status === 'submitted' || w.status === 'approved') ? (
                <button
                  type="button"
                  onClick={() => {
                    const note = window.prompt('What must the Field Officer correct?');
                    if (note !== null) void mutate(() => api.returnWeek(w.id, note));
                  }}
                  style={sx(
                    'background:#fff;color:#a3372c;border:1px solid #e3c9c5;padding:9px 14px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer',
                  )}
                >
                  Return
                </button>
              ) : null}
              {canEntry && (w.status === 'draft' || w.status === 'returned') ? (
                <button
                  type="button"
                  onClick={() => void mutate(() => api.submitWeek(w.id))}
                  style={sx(
                    'background:#0f5c52;color:#fff;border:0;padding:9px 14px;border-radius:4px;font-size:13px;font-weight:600;cursor:pointer',
                  )}
                >
                  Submit
                </button>
              ) : null}
            </div>
          </div>
        );
      })}

      {rows.length === 0 ? <Empty pad="36px">No weekly returns have been recorded yet.</Empty> : null}
    </div>
  );
}
